import React, { useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { BaseExportParams } from 'ag-grid-community';
import { DateTime } from 'luxon';
import { LogControlName } from '../utils/log.utils';
import { useCreateLogApi } from './api/log.hook';
import { useMessageModal } from './modal.hook';
import { ApiError } from '../services/http';
import AGUtils from '../utils/ag-grid.utils';

interface UseExportAndLogParams {
  tableRef?: React.RefObject<AgGridReact>,
  logFormName?: string;
  baseExportParams?: BaseExportParams
  exportFileNamePrefix?: string;
  exportFeatureName?: string;
  exportSheetName?: string;
  exportExcludeColIds?: string[];
  hideExportMenu?: boolean;
}

function useExportAndLog({
  tableRef, logFormName, baseExportParams, exportFileNamePrefix, exportFeatureName, exportSheetName, exportExcludeColIds, hideExportMenu,
}: UseExportAndLogParams) {
  const { request: createLog } = useCreateLogApi();
  const openMessageModal = useMessageModal();

  const createLogOnDownloadTableData = useCallback(
    (_logFormName: string) => createLog(_logFormName, LogControlName.Download),
    [createLog],
  );

  const buildFileName = useCallback(() => {
    if (exportFeatureName) return AGUtils.exportFileName(exportFeatureName);
    const prefix = exportFileNamePrefix ?? logFormName;
    if (!prefix) return baseExportParams?.fileName;
    return `${prefix}_${DateTime.now().toFormat('yyyy_MM_dd HH_mm_ss')}`;
  }, [exportFeatureName, exportFileNamePrefix, logFormName, baseExportParams]);

  const buildColumnKeys = useCallback(() => {
    if (!exportExcludeColIds?.length) return undefined;
    const displayedColumns = tableRef?.current?.columnApi?.getAllDisplayedColumns() ?? [];
    return displayedColumns
      .map((column) => column.getColId())
      .filter((colId) => !exportExcludeColIds.includes(colId));
  }, [exportExcludeColIds, tableRef]);

  const handleExportCsv = useCallback(async () => {
    if (tableRef && tableRef.current && tableRef.current.api) {
      if (logFormName) {
        try {
          await createLogOnDownloadTableData(logFormName);
        } catch (e) {
          openMessageModal((e as ApiError)?.message);
          return;
        }
      }
      const columnKeys = buildColumnKeys();
      tableRef.current.api.exportDataAsCsv({ ...baseExportParams, fileName: buildFileName(), ...(columnKeys ? { columnKeys } : {}) });
    }
  }, [createLogOnDownloadTableData, openMessageModal, tableRef, logFormName, baseExportParams, buildFileName, buildColumnKeys]);

  const handleExportExcel = useCallback(async () => {
    if (tableRef && tableRef.current && tableRef.current.api) {
      if (logFormName) {
        try {
          await createLogOnDownloadTableData(logFormName);
        } catch (e) {
          openMessageModal((e as ApiError)?.message);
          return;
        }
      }
      const columnKeys = buildColumnKeys();
      tableRef.current?.api.exportDataAsExcel({
        ...baseExportParams, fileName: buildFileName(), sheetName: exportSheetName, ...(columnKeys ? { columnKeys } : {}),
      });
    }
  }, [createLogOnDownloadTableData, openMessageModal, tableRef, logFormName, baseExportParams, buildFileName, exportSheetName, buildColumnKeys]);

  const getContextMenuItems = useCallback(() => {
    const defaultItems = [
      'copy',
      'copyWithHeaders',
      'copyWithGroupHeaders',
      'paste',
      'separator',
    ];

    const exportMenuItems = [
      {
        name: 'CSV Export',
        action: handleExportCsv,
        icon: '<span class="ag-icon ag-icon-csv"></span>',
      },
      {
        name: 'Excel Export',
        action: handleExportExcel,
        icon: '<span class="ag-icon ag-icon-excel"></span>',
      },
    ];

    if (hideExportMenu) {
      return defaultItems.filter((item) => item !== 'separator');
    }

    return [...defaultItems, {
      name: 'Export',
      subMenu: exportMenuItems,
      icon: '<span class="ag-icon ag-icon-save"></span>',
    }];
  }, [handleExportExcel, handleExportCsv, hideExportMenu]);

  return {
    getContextMenuItems,
  };
}

export default useExportAndLog;
